import React from 'react';
import { motion } from 'framer-motion';
import { useProfile } from '../../contexts/ProfileContext';
import { 
  AcademicCapIcon, 
  BriefcaseIcon, 
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { Tab } from '@headlessui/react';
import { usePageTitle } from '../../hooks/usePageTitle';

const AboutPage: React.FC = () => {
  usePageTitle("About");
  
  const { profile, loading } = useProfile();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Default empty arrays if undefined
  const approachItems = profile.approachItems || [];
  const softSkills = profile.softSkills || [];
  const researchInterests = profile.researchInterests || [];
  const teachingExperience = profile.teachingExperience || [];
  const certifications = profile.certifications || [];
  const industryKnowledge = profile.industryKnowledge || [];

  return (
    <div className="container py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="section-title">About Me</h1>
        
        {/* Enhanced Personal Statement */}
        {profile.personalStatement && (
          <motion.div 
            className="relative mb-16"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Background decorative elements */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-blue-500/10 to-purple-500/10 dark:from-primary-900/20 dark:via-blue-900/20 dark:to-purple-900/20 rounded-3xl transform rotate-1"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-primary-500/10 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-primary-900/20 rounded-3xl transform -rotate-1"></div>
            
            {/* Main content */}
            <div className="relative bg-white dark:bg-gray-800 p-12 md:p-12 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm">
              {/* Decorative quote icon */}
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-primary-500 to-blue-500 p-4 rounded-full shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                  </svg>
                </div>
              </div>

              <div className="text-center mt-2">
                <div className="mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                    Personal Statement
                  </h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-blue-500 mx-auto rounded-full"></div>
                </div>
                
                <blockquote className="relative">
                  {/* Large decorative quotes */}
                  <span className="absolute -top-8 -left-4 text-8xl text-primary-200 dark:text-primary-800 font-serif leading-none select-none">"</span>
                  <span className="absolute -bottom-16 -right-4 text-8xl text-primary-200 dark:text-primary-800 font-serif leading-none select-none">"</span>
                  
                  <p className="text-xl md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-5xl mx-auto font-light relative z-10 italic">
                    {profile.personalStatement}
                  </p>
                </blockquote>

                {/* Signature or accent line */}
                <div className="mt-12 flex items-center justify-center space-x-4">
                  <div className="h-px w-16 bg-gradient-to-r from-transparent to-primary-500"></div>
                  <div className="w-3 h-3 bg-gradient-to-r from-primary-500 to-blue-500 rounded-full"></div>
                  <div className="h-px w-16 bg-gradient-to-r from-primary-500 to-transparent"></div>
                </div>
              </div>

              {/* Subtle pattern overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-blue-50/50 dark:from-primary-900/10 dark:via-transparent dark:to-blue-900/10 rounded-3xl pointer-events-none"></div>
            </div>
          </motion.div>
        )}

        {/* Tabbed Information Sections */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Tab.Group>
            <Tab.List className="flex rounded-2xl bg-white dark:bg-gray-800 p-2 shadow-lg border border-gray-200 dark:border-gray-600 overflow-x-auto">
              <Tab
                className={({ selected }) =>
                  `flex-1 py-4 px-6 text-sm font-semibold leading-5 rounded-xl transition-all duration-300 focus:outline-none min-w-0 ${
                    selected
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg text-white transform scale-105'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                  }`
                }
              >
                <div className="flex items-center justify-center space-x-2">
                  <BriefcaseIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">Professional</span>
                </div>
              </Tab>
              <Tab
                className={({ selected }) =>
                  `flex-1 py-4 px-6 text-sm font-semibold leading-5 rounded-xl transition-all duration-300 focus:outline-none min-w-0 ${
                    selected
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg text-white transform scale-105'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                  }`
                }
              >
                <div className="flex items-center justify-center space-x-2">
                  <AcademicCapIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">Academic</span>
                </div>
              </Tab>
              <Tab
                className={({ selected }) =>
                  `flex-1 py-4 px-6 text-sm font-semibold leading-5 rounded-xl transition-all duration-300 focus:outline-none min-w-0 ${
                    selected
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg text-white transform scale-105'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                  }`
                }
              >
                <div className="flex items-center justify-center space-x-2">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">Certifications</span>
                </div>
              </Tab>
            </Tab.List>
            <Tab.Panels className="mt-8">
              {/* Professional Panel */}
              <Tab.Panel>
                <motion.div 
                  className="rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-600 overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6">
                    <h2 className="text-2xl font-bold text-white">Professional Profile</h2>
                    <p className="text-primary-100 mt-2">Expertise, skills, and professional journey</p>
                  </div>
                  
                  <div className="p-8 space-y-8">
                    {/* Bio Section */}
                    {profile.bio && (
                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl border border-gray-200 dark:border-gray-600">
                        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                          About Me
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                          {profile.bio}
                        </p>
                      </div>
                    )}

                    {/* My Approach Section */}
                    {approachItems.length > 0 && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl border border-green-200 dark:border-gray-600">
                        <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                          My Approach
                        </h3>
                        <div className="grid gap-4">
                          {approachItems.map((item, index) => (
                            <motion.div 
                              key={item.id}
                              className="flex items-start p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center mr-4">
                                <span className="text-white font-bold text-sm">{index + 1}</span>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h4>
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{item.description}</p>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Soft Skills */}
                    {softSkills.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                          Core Competencies
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {softSkills.map((skill, index) => (
                            <motion.div 
                              key={skill.id} 
                              className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 p-5 rounded-xl border border-blue-200 dark:border-gray-600 hover:shadow-lg transition-all duration-300"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                              <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-3 text-lg">{skill.title}</h4>
                              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{skill.description}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Industry Knowledge */}
                    {industryKnowledge.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                          Industry Expertise
                        </h3>
                        <div className="flex flex-wrap gap-3">
                          {industryKnowledge.map((knowledge, index) => (
                            <motion.span 
                              key={index}
                              className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 text-purple-800 dark:text-purple-200 px-4 py-2 rounded-full text-sm font-medium border border-purple-200 dark:border-purple-700 hover:shadow-md transition-all duration-300"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                              {knowledge}
                            </motion.span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Professional Profiles */}
                    <div>
                      <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                        Professional Networks
                      </h3>
                      <div className="flex flex-wrap gap-4">
                        {profile.socialLinks?.linkedin && (
                          <a 
                            href={profile.socialLinks.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-gradient-to-r from-[#0077B5] to-[#005885] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.454C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/>
                            </svg>
                            LinkedIn Profile
                          </a>
                        )}
                        
                        {profile.socialLinks?.github && (
                          <a 
                            href={profile.socialLinks.github} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-gradient-to-r from-[#333] to-[#24292e] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                            </svg>
                            GitHub Profile
                          </a>
                        )}
                        
                        {profile.socialLinks?.website && (
                          <a 
                            href={profile.socialLinks.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9"/>
                            </svg>
                            Personal Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Tab.Panel>

              {/* Academic Panel */}
              <Tab.Panel>
                <motion.div 
                  className="rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-600 overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
                    <h2 className="text-2xl font-bold text-white">Academic Excellence</h2>
                    <p className="text-blue-100 mt-2">Research, teaching, and scholarly contributions</p>
                  </div>
                  
                  <div className="p-8 space-y-8">
                    {/* Research Summary */}
                    {profile.researchSummary && (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl border border-blue-200 dark:border-gray-600">
                        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                          Research Focus
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                          {profile.researchSummary}
                        </p>
                      </div>
                    )}

                    {/* Research Interests */}
                    {researchInterests.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                          Research Interests
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {researchInterests.map((interest, index) => (
                            <motion.div 
                              key={interest.id} 
                              className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 p-5 rounded-xl border border-indigo-200 dark:border-gray-600 hover:shadow-lg transition-all duration-300"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                              <h4 className="font-semibold text-indigo-700 dark:text-indigo-400 mb-3 text-lg">{interest.title}</h4>
                              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{interest.description}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Teaching Experience */}
                    {teachingExperience.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                          Teaching Experience
                        </h3>
                        <div className="space-y-4">
                          {teachingExperience.map((experience, index) => (
                            <motion.div 
                              key={experience.id} 
                              className="border-l-4 border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 p-5 rounded-r-xl"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white text-lg mb-2">{experience.title}</h4>
                              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{experience.description}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mentorship Experience */}
                    {profile.mentorshipExperience && (
                      <div>
                        <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                          Mentorship Experience
                        </h3>
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl border border-yellow-200 dark:border-gray-600">
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">{profile.mentorshipExperience}</p>
                        </div>
                      </div>
                    )}

                    {/* Academic Profiles */}
                    <div>
                      <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                        Academic Networks
                      </h3>
                      <div className="flex flex-wrap gap-4">
                        {profile.socialLinks?.googleScholar && (
                          <a 
                            href={profile.socialLinks.googleScholar} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-gradient-to-r from-[#4285F4] to-[#1a73e8] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.748-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z"/>
                            </svg>
                            Google Scholar
                          </a>
                        )}
                        
                        {profile.socialLinks?.researchGate && (
                          <a 
                            href={profile.socialLinks.researchGate} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-gradient-to-r from-[#00CCBB] to-[#00a99d] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M19.586 0c-.818 0-1.508.19-2.073.565-.563.377-.97.936-1.213 1.68a3.193 3.193 0 0 0-.112.437 8.365 8.365 0 0 0-.078.53 9 9 0 0 0-.05.727c-.01.282-.013.621-.013 1.016a31.121 31.121 0 0 0 .014 1.017 9 9 0 0 0 .05.727 7.946 7.946 0 0 0 .077.53h-.005a3.334 3.334 0 0 0 .113.438c.245.743.65 1.303 1.214 1.68.565.376 1.256.564 2.075.564.8 0 1.536-.213 2.105-.603.57-.39.94-.916 1.175-1.65.076-.235.135-.558.177-.93.043-.372.064-.938.064-1.696 0-.757-.021-1.323-.064-1.696-.042-.371-.101-.695-.177-.93-.234-.734-.602-1.26-1.17-1.648-.569-.39-1.298-.583-2.11-.583zM7.996.006c-.857 0-1.635.182-2.33.565-.697.384-1.252.988-1.683 1.808-.82.156-.146.33-.205.519a6.82 6.82 0 0 0-.147.585 7.075 7.075 0 0 0-.09.678 8.922 8.922 0 0 0-.017.792c-.004.262 0 .568.008.903.01.334.022.703.041 1.103l-.002.002a17.815 17.815 0 0 0 .141 1.489c.059.433.123.833.191 1.186.214 1.084.5 2.172.875 3.14a10.88 10.88 0 0 0 1.148 2.284 5.224 5.224 0 0 0 1.418 1.488c.76.545 1.53.814 2.312.814a3.658 3.658 0 0 0 1.623-.378 3.453 3.453 0 0 0 1.278-1.012 6.448 6.448 0 0 0 .878-1.569c.237-.595.444-1.228.608-1.908.164-.68.284-1.328.357-1.926.073-.598.123-1.153.141-1.648.018-.495.027-.914.027-1.25 0-.468-.009-.86-.027-1.185a9.578 9.578 0 0 0-.09-.865 3.659 3.659 0 0 0-.233-.825c-.142-.329-.356-.6-.636-.812-.28-.212-.655-.33-1.142-.378-.596-.044-1.142.088-1.644.378-.503.29-.943.726-1.319 1.302.066-1.259.018-2.145-.103-2.677-.12-.533-.295-.886-.521-1.055-.227-.17-.506-.254-.836-.254zm11.595 3.344c.442 0 .797.157 1.056.47.26.314.444.793.55 1.437.106.645.158 1.487.158 2.525 0 1.038-.053 1.88-.159 2.525-.106.644-.29 1.123-.549 1.436-.26.314-.614.471-1.056.471-.44 0-.795-.157-1.056-.47-.26-.314-.442-.793-.55-1.437-.106-.645-.158-1.487-.158-2.525 0-1.038.053-1.88.158-2.525.108-.644.29-1.123.55-1.436.26-.314.615-.471 1.056-.471zM7.375 4.969c.255 0 .509.145.764.47.254.327.479.806.678 1.437.2.632.352 1.424.461 2.376.101.87.186 1.71.254 2.525.214.64.335 1.282.352 1.926.018.645-.037 1.24-.164 1.786-.127.546-.323 1.019-.602 1.39-.279.371-.652.573-1.102.602-.44.028-.806-.073-1.102-.3-.296-.227-.537-.532-.724-.903-.188-.371-.34-.819-.444-1.336a21.275 21.275 0 0 1-.226-1.659 16.43 16.43 0 0 1-.09-1.75c0-.67.013-1.283.042-1.84a16.855 16.855 0 0 1 .158-1.587c.07-.496.158-.903.26-1.214.102-.31.233-.591.391-.84.159-.25.347-.448.567-.592.221-.146.486-.225.787-.263.064-.01.128-.017.19-.023a.718.718 0 0 1 .1-.005z"/>
                            </svg>
                            ResearchGate
                          </a>
                        )}
                        
                        {profile.socialLinks?.orcid && (
                          <a 
                            href={profile.socialLinks.orcid} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-gradient-to-r from-[#A6CE39] to-[#8fb832] text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zM7.369 4.378c.525 0 .947.431.947.947s-.422.947-.947.947a.95.95 0 0 1-.947-.947c0-.525.422-.947.947-.947zm-.722 3.038h1.444v10.041H6.647V7.416zm3.562 0h3.9c3.712 0 5.344 2.653 5.344 5.025 0 2.578-2.016 5.025-5.325 5.025h-3.919V7.416zm1.444 1.303v7.444h2.297c3.272 0 4.022-2.484 4.022-3.722 0-2.016-1.284-3.722-4.097-3.722h-2.222z"/>
                            </svg>
                            ORCID ID
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Tab.Panel>

              {/* Certifications Panel */}
              <Tab.Panel>
                <motion.div 
                  className="rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-600 overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {certifications.length > 0 ? (
                    <>
                      <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6">
                        <h2 className="text-2xl font-bold text-white">Professional Certifications</h2>
                        <p className="text-green-100 mt-2">Verified credentials and achievements</p>
                      </div>
                      
                      <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {certifications.map((cert, index) => (
                            <motion.div 
                              key={cert.id} 
                              className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl shadow-lg border border-green-200 dark:border-gray-600 hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                  </svg>
                                </div>
                              </div>
                              
                              <h4 className="font-bold text-xl mb-3 text-gray-900 dark:text-white">{cert.name}</h4>
                              <div className="space-y-2 mb-4">
                                <p className="text-gray-600 dark:text-gray-400">
                                  <span className="font-semibold text-gray-800 dark:text-gray-200">Issuer:</span> {cert.issuer}
                                </p>
                                <p className="text-gray-600 dark:text-gray-400">
                                  <span className="font-semibold text-gray-800 dark:text-gray-200">Issued:</span> {cert.date}
                                </p>
                              </div>
                              
                              {cert.url && (
                                <a 
                                  href={cert.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                                >
                                  <span>View Certificate</span>
                                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-16 text-center">
                      <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-500 dark:text-gray-400 mb-2">No Certifications Yet</h3>
                      <p className="text-gray-400 dark:text-gray-500">Professional certifications will be displayed here when available.</p>
                    </div>
                  )}
                </motion.div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </motion.div>


      </motion.div>
    </div>
  );
};

export default AboutPage; 